import React, { useEffect, useCallback, useRef } from "react";
import classes from "./BaseModal.module.css";
import Backdrop from "./Backdrop/Backdrop";

/**
 * Props:
 * - isOpen: boolean - Controls modal visibility
 * - onClose: function - Called when modal should close
 * - title: string - Modal title (optional)
 * - size: 'default' | 'big' | 'ultrawide' - Modal width (optional)
 * - children: ReactNode - Modal content
 * - className: string - Additional CSS class for the modal container (optional)
 * - unescapable: boolean - If true, modal cannot be closed by Escape key or clicking backdrop (optional)
 */
const BaseModal = ({ 
  isOpen, 
  onClose, 
  title, 
  size = 'default',
  children,
  className = '',
  unescapable = false
}) => {
  const modalRef = useRef(null);

  // Handle Escape key press
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape' && isOpen && onClose && !unescapable) {
      onClose();
    }
  }, [isOpen, onClose, unescapable]);

  // Add/remove keyboard listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Determine modal size class
  const getSizeClass = () => {
    switch (size) {
      case 'ultrawide':
        return classes.ultrawide;
      case 'big':
        return classes.bigmodal;
      default:
        return '';
    }
  };

  const modalClass = [
    classes.Modal,
    getSizeClass(),
    className
  ].filter(Boolean).join(' ');

  if (!isOpen) return null;

  return (
    <>
      <Backdrop show={isOpen} wasClicked={unescapable ? undefined : onClose}/>
      <div 
        ref={modalRef}
        className={modalClass}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
      >
        {title && (
          <h2 id="modal-title" className={classes.title}>{title}</h2>
        )}
        {children}
      </div>
    </>
  );
};

/**
 * Provides consistent spacing and layout for buttons
 */
export const ModalActions = ({ children, className = '' }) => (
  <div className={[classes.buttonRow, className].filter(Boolean).join(' ')}>
    {children}
  </div>
);

export const SaveButton = ({ 
  onClick, 
  disabled = false, 
  children = "Save",
  className = ''
}) => (
  <button 
    className={[
      disabled ? classes.saveButtonDisabled : classes.saveButton,
      className
    ].filter(Boolean).join(' ')}
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

export const CancelButton = ({ 
  onClick, 
  children = "Cancel",
  className = ''
}) => (
  <button 
    className={[classes.cancelButton, className].filter(Boolean).join(' ')}
    onClick={onClick}
  >
    {children}
  </button>
);

export const DeleteButton = ({ 
  onClick, 
  children = "Delete",
  className = ''
}) => (
  <button 
    className={[classes.deleteButton, className].filter(Boolean).join(' ')}
    onClick={onClick}
  >
    {children}
  </button>
);

export default BaseModal;
